import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/apiService';

// Set when a 401 ends a session that existed; read and cleared by the login
// screen so it can explain itself. sessionStorage, not localStorage: the
// explanation is only true for this tab, and only until it is read.
export const SESSION_ENDED_KEY = 'imgaff_session_ended';

// Floor between profile re-reads while verification is unsettled. Long enough
// that flicking between tabs cannot spam the endpoint, short enough that someone
// waiting on an approval sees it on their next real return to the tab.
export const VERIFICATION_RECHECK_MIN_MS = 60_000;

interface User {
  id: number;
  email: string;
  name?: string;
  role: 'admin' | 'user';
  status?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, websiteUrl?: string) => Promise<void>;
  loginWithAuditToken: (linkToken: string) => Promise<number | null>;
  logout: () => void;
  loading: boolean;
  isAuthenticated: boolean;
  isUser: boolean;
  verificationStatus: string | null;
  refreshProfile: () => Promise<void>;
  // feature-flag-registry: keys currently on for this account, carried on
  // GET /api/auth/profile (the fetch AuthContext already makes on load and
  // after login/signup) rather than a dedicated request. Stale until the
  // next fetchProfile — a flip reaches an open tab only on reload/refresh.
  flags: string[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState<string | null>(null);
  const [flags, setFlags] = useState<string[]>([]);

  const fetchProfile = async () => {
    try {
      const profileData = await apiService.getProfile();
      setVerificationStatus(profileData.profile?.verificationStatus || null);
      setFlags(profileData.flags ?? []);
    } catch {
      // Profile fetch failure is non-fatal
    }
  };

  // Single owner of auth invalidation: used by logout and the 401 handler
  const clearAuthState = () => {
    setUser(null);
    setToken(null);
    setVerificationStatus(null);
    setFlags([]);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
  };

  useEffect(() => {
    // Check for stored token on app load
    const storedToken = localStorage.getItem('accessToken');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
        // Fetch profile to get current verification status
        fetchProfile();
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
      }
    }

    setLoading(false);
  }, []);

  // AN ADMIN CAN VERIFY AN ACCOUNT OUT OF BAND, and until this the publisher was
  // never told. `POST /api/admin/users/:id/approve` sets
  // user_profiles.verification_status server-side; the portal reads it from the
  // profile, fetched at login and after the publisher's own successful verify —
  // so a publisher sitting on the dashboard while an operator approves them kept
  // being told to verify their site, with a checklist blocking every later step,
  // until they happened to reload.
  //
  // Re-read on becoming visible, which is when a person who has been waiting for
  // an approval comes back to the tab. Two gates, because a refresh on every tab
  // switch for every publisher forever is not worth one rare transition:
  //   - only while verification is UNSETTLED. Once verified, this costs nothing
  //     ever again; a null status (profile not loaded, or its fetch failed) is
  //     also skipped, since there is nothing to correct.
  //   - at most once a minute, so flicking between tabs cannot spam the endpoint.
  //
  // `visibilitychange` rather than `focus`: it is the one that fires when a phone
  // returns to the browser, and mobile is where this waiting happens.
  useEffect(() => {
    if (!token) return;
    if (verificationStatus == null || verificationStatus === 'verified') return;

    let lastAt = 0;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastAt < VERIFICATION_RECHECK_MIN_MS) return;
      lastAt = now;
      fetchProfile();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, verificationStatus]);

  useEffect(() => {
    // apiService dispatches this when a real-mode API call returns 401.
    //
    // The marker is what lets the login screen say WHY it is showing. Without
    // it a publisher mid-task is dropped onto a card headed "Welcome back"
    // with no indication anything happened. Set only when a session actually
    // existed, so a 401 with no stored token cannot announce an expiry that
    // never occurred — and NOT inside clearAuthState, which the sign-out
    // button also calls, where "your session ended" would be a lie.
    const onUnauthorized = () => {
      if (localStorage.getItem('accessToken')) {
        sessionStorage.setItem(SESSION_ENDED_KEY, '1');
      }
      clearAuthState();
    };
    window.addEventListener('ezaff:unauthorized', onUnauthorized);
    return () => window.removeEventListener('ezaff:unauthorized', onUnauthorized);
  }, []);

  const setAuthState = (authToken: string, authUser: User) => {
    setToken(authToken);
    setUser(authUser);
    localStorage.setItem('accessToken', authToken);
    localStorage.setItem('user', JSON.stringify(authUser));
  };

  const login = async (email: string, password: string): Promise<void> => {
    try {
      const response = await apiService.login(email, password);
      setAuthState(response.token, response.user as User);
      fetchProfile();
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // Open a session from a single-use audit magic link. Deliberately installs
  // state through the same setAuthState as login, so a magic-link session is
  // an ordinary session in every respect — including a `pending` status that
  // still gates feeds behind website verification.
  const loginWithAuditToken = async (linkToken: string): Promise<number | null> => {
    const response = await apiService.openAuditSession(linkToken);
    setAuthState(response.token, response.user as User);
    fetchProfile();
    return response.reportId;
  };

  const signup = async (email: string, password: string, websiteUrl?: string): Promise<void> => {
    try {
      const response = await apiService.signup(email, password, websiteUrl);
      setAuthState(response.token, response.user as User);
      fetchProfile();
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  };

  const logout = clearAuthState;

  const refreshProfile = async () => {
    await fetchProfile();
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    signup,
    loginWithAuditToken,
    logout,
    loading,
    isAuthenticated: !!user && !!token,
    isUser: !!user, // Any authenticated user can access user interface
    verificationStatus,
    refreshProfile,
    flags,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
